import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Wrench } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { useAuth } from '../lib/AuthContext';
import { errMsg } from '../lib/api';

export default function LoginPage() {
  const { login, config } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const me = await login(email.trim(), password);
      if (me.user.is_platform_admin) navigate('/admin');
      else if (me.user.role === 'technician') navigate('/tech');
      else if (me.user.role === 'owner') navigate('/dashboard');
      else navigate('/board');
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-dvh flex items-center justify-center px-4 bg-[radial-gradient(1200px_circle_at_20%_0%,rgba(14,165,164,0.10),transparent_55%),radial-gradient(900px_circle_at_90%_10%,rgba(253,231,179,0.30),transparent_50%)]">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center space-y-2">
          <span className="inline-flex h-12 w-12 rounded-2xl bg-[hsl(var(--primary))] text-white items-center justify-center shadow-[var(--shadow-soft)]">
            <Wrench className="h-6 w-6" />
          </span>
          <h1 className="text-2xl font-semibold font-display">{config?.app_name || 'Auto Pampanga'}</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Run your repair shop from your phone</p>
        </div>
        <Card className="rounded-2xl shadow-[var(--shadow-card)] border-black/5">
          <CardContent className="pt-6">
            <form onSubmit={submit} className="space-y-3">
              <Input data-testid="login-email-input" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 rounded-xl" />
              <Input data-testid="login-password-input" type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 rounded-xl" />
              <Button data-testid="login-submit-button" type="submit" disabled={busy} className="w-full h-12 rounded-xl text-base">
                {busy ? 'Logging in…' : 'Log in'}
              </Button>
              {config?.password_reset_enabled && (
                <p className="text-center text-sm pt-1">
                  <Link data-testid="login-forgot-link" to="/forgot-password" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]">
                    Forgot password?
                  </Link>
                </p>
              )}
            </form>
          </CardContent>
        </Card>
        <p className="text-center text-sm text-[hsl(var(--muted-foreground))]">
          New shop?{' '}
          <Link data-testid="login-signup-link" to="/signup" className="font-medium text-[hsl(var(--primary))]">
            Start your free 60-day trial
          </Link>
        </p>
      </div>
    </div>
  );
}
